const RANK = { ok: 0, unknown: 1, pace: 2, park: 3 };
const name = (c) => (c.label ? `${c.label} (${c.localId})` : c.localId);
const at = (t) => new Date(t).toISOString();
/** Earliest upcoming reset among the account's windows (capacity that expires soonest). */
function nextReset(b, now) {
    let best;
    for (const w of b.windows)
        if (w.resetsAt !== undefined && w.resetsAt > now && (best === undefined || w.resetsAt < best))
            best = w.resetsAt;
    return best;
}
function gate(c, input) {
    if (!c.available)
        return {
            ok: false,
            reason: 'reconnect',
            localId: c.localId,
            explanation: [
                `${name(c)} is not signed in${c.unavailableReason ? ` (${c.unavailableReason})` : ''}; reconnect it on its machine`,
            ],
        };
    const budget = input.budget(c.usageKey, input.agent);
    if (budget.state === 'park')
        return {
            ok: false,
            reason: 'usage_parked',
            localId: c.localId,
            ...(budget.parkUntil !== undefined ? { until: budget.parkUntil } : {}),
            explanation: [`${name(c)}: ${budget.reasons[0] ?? 'out of quota'}`],
        };
    const load = input.load(c.usageKey);
    if (budget.maxConcurrent !== null && load >= budget.maxConcurrent)
        return {
            ok: false,
            reason: 'usage_paced',
            localId: c.localId,
            explanation: [
                `${name(c)}: paced ${load}/${budget.maxConcurrent} — ${budget.reasons[0] ?? ''}`.trim(),
            ],
        };
    return { ok: true, budget };
}
/**
 * Pick the provider account for a new run.
 *
 * - An explicit choice is binding: when it cannot run now the result says why (reconnect /
 *   parked until reset / paced); another account is never substituted.
 * - Otherwise, among signed-in accounts (aliases of one upstream account count once): healthiest
 *   budget state first, then the soonest window reset (use capacity before it expires), then the
 *   most headroom, then a stable ID order. Every decision carries a human-readable explanation.
 */
export function chooseAccount(input) {
    if (input.explicit !== undefined) {
        const c = input.candidates.find((x) => x.localId === input.explicit);
        if (!c)
            return {
                ok: false,
                reason: 'no_account',
                localId: input.explicit,
                explanation: [
                    `selected account ${input.explicit} is not on this machine; not substituting`,
                ],
            };
        const g = gate(c, input);
        if (!g.ok)
            return { ...g, explanation: [...g.explanation, 'explicit choice: no automatic fallback'] };
        return {
            ok: true,
            localId: c.localId,
            usageKey: c.usageKey,
            budget: g.budget,
            explanation: [`${name(c)}: explicitly selected; ${g.budget.state}`],
        };
    }
    const seen = new Set();
    const eligible = [];
    const refused = [];
    for (const c of input.candidates) {
        if (c.available && seen.has(c.usageKey))
            continue; // Alias of an account already considered.
        const g = gate(c, input);
        if (!g.ok) {
            refused.push(g);
            continue;
        }
        seen.add(c.usageKey);
        eligible.push({ c, budget: g.budget });
    }
    if (!eligible.length) {
        if (!refused.length)
            return {
                ok: false,
                reason: 'no_account',
                explanation: [`no ${input.agent} account on this machine`],
            };
        // Prefer the soonest recoverable state: paced > parked (with the earliest reset) > reconnect.
        const paced = refused.find((r) => r.reason === 'usage_paced');
        const parked = refused
            .filter((r) => r.reason === 'usage_parked')
            .sort((a, b) => (a.until ?? Infinity) - (b.until ?? Infinity))[0];
        const pick = paced ?? parked ?? refused[0];
        return {
            ...pick,
            explanation: [
                ...refused.flatMap((r) => r.explanation),
                pick.reason === 'usage_parked' && pick.until !== undefined
                    ? `every ${input.agent} account is exhausted; earliest reset ${at(pick.until)}`
                    : `no ${input.agent} account can take a new run now`,
            ],
        };
    }
    eligible.sort((x, y) => {
        const r = RANK[x.budget.state] - RANK[y.budget.state];
        if (r)
            return r;
        const rx = nextReset(x.budget, input.now) ?? Infinity;
        const ry = nextReset(y.budget, input.now) ?? Infinity;
        if (rx !== ry)
            return rx - ry;
        const hx = x.budget.headroomPct ?? -Infinity;
        const hy = y.budget.headroomPct ?? -Infinity;
        if (hx !== hy)
            return hy - hx;
        return x.c.localId < y.c.localId ? -1 : x.c.localId > y.c.localId ? 1 : 0;
    });
    const best = eligible[0];
    const reset = nextReset(best.budget, input.now);
    const explanation = [
        `${name(best.c)}: ${best.budget.state}` +
            (best.budget.headroomPct !== null
                ? `, ${Math.round(best.budget.headroomPct)}% headroom`
                : '') +
            (reset !== undefined ? `, next reset ${at(reset)}` : ''),
    ];
    const others = eligible.slice(1).map((e) => `${name(e.c)} ${e.budget.state}`);
    if (others.length)
        explanation.push(`also eligible: ${others.join(', ')}`);
    for (const r of refused)
        explanation.push(`skipped ${r.explanation[0]}`);
    return {
        ok: true,
        localId: best.c.localId,
        usageKey: best.c.usageKey,
        budget: best.budget,
        explanation,
    };
}
/**
 * A live run's account hit its limit. Switching a running task to another account is a visible
 * handoff and happens automatically only when autonomy allows it; otherwise the run parks until
 * the reset. Either way the notice says exactly what happened and what will happen next.
 */
export function onAccountExhausted(input) {
    const who = name(input.account);
    const resume = input.parkUntil !== undefined
        ? `resumes automatically at ${at(input.parkUntil)}`
        : 'resumes when the provider accepts work again';
    const alt = input.alternative?.ok ? input.alternative : undefined;
    if (alt && input.autoSwitchAccount)
        return {
            action: 'handoff',
            to: alt.localId,
            notice: `${who} reached its usage limit; handing off to ${alt.localId} as your autonomy settings allow.`,
        };
    return {
        action: 'park',
        ...(input.parkUntil !== undefined ? { until: input.parkUntil } : {}),
        notice: `${who} reached its usage limit; the run is parked and ${resume}.`,
        ...(alt ? { offer: `Continue on ${alt.localId} now (handoff)` } : {}),
    };
}
//# sourceMappingURL=account-choice.js.map